import type { components } from "@scalty/contracts";

export type Accelerator = components["schemas"]["Accelerator"];
export type AcceleratorClass = components["schemas"]["AcceleratorClass"];
export type AcceleratorEstimate = components["schemas"]["AcceleratorEstimate"];
export type AcceleratorProjection = components["schemas"]["AcceleratorProjection"];
export type TrainRequest = components["schemas"]["TrainRequest"];
type ModelEnvelope = components["schemas"]["ModelEnvelope"];

export interface AcceleratorSelection {
  acceleratorClass: AcceleratorClass;
  acceleratorCount: number;
}

const CLASS_LABEL: Record<AcceleratorClass, string> = {
  h200: "H200",
  b200: "B200",
  a100: "A100",
  a100_80gb: "A100 80 GB",
  l4: "L4",
  rtx_pro_6000: "RTX PRO 6000",
};

export function acceleratorLabel(acceleratorClass: AcceleratorClass): string {
  return CLASS_LABEL[acceleratorClass];
}

export function formatMachine(selection: AcceleratorSelection): string {
  return `${acceleratorLabel(selection.acceleratorClass)} × ${selection.acceleratorCount}`;
}

export function availableAccelerators(accelerators: Accelerator[]): Accelerator[] {
  return accelerators.filter((accelerator) => accelerator.available && accelerator.counts.length > 0);
}

export function defaultAccelerator(accelerators: Accelerator[]): Accelerator | null {
  const available = availableAccelerators(accelerators);
  return available.find((accelerator) => accelerator.default) ?? null;
}

/**
 * The projection for the training set the dialog is about to train, when the
 * API could derive one. It replaces the median everywhere a figure is shown:
 * the median answers "what have runs on this machine taken", which is not a
 * question about this set.
 */
export function estimateProjection(estimate: AcceleratorEstimate): AcceleratorProjection | null {
  return estimate.state === "measured" ? estimate.projection : null;
}

/** The cents a tier comparison ranks on: this set's projection, else the median. */
function estimateCostCents(estimate: AcceleratorEstimate): number | null {
  if (estimate.state !== "measured") return null;
  const projection = estimate.projection;
  if (projection !== null) return projection.estimatedCost?.amountCents ?? null;
  return estimate.estimatedCost?.amountCents ?? null;
}

export function economyAccelerator(accelerators: Accelerator[]): Accelerator | null {
  const primary = defaultAccelerator(accelerators);
  const cheapest = availableAccelerators(accelerators)
    .filter((accelerator) => estimateCostCents(accelerator.estimate) !== null)
    .sort((left, right) => {
      const cost = estimateCostCents(left.estimate)! - estimateCostCents(right.estimate)!;
      if (cost !== 0) return cost;
      return left.acceleratorClass.localeCompare(right.acceleratorClass);
    })[0] ?? null;
  // Showing the default machine twice would make both tier cards appear
  // selected. When it is already the cheapest measured choice, Fast alone is
  // truthful and the machine list still exposes every row.
  return cheapest?.acceleratorClass === primary?.acceleratorClass ? null : cheapest;
}

export function initialAcceleratorSelection(
  accelerators: Accelerator[],
): AcceleratorSelection | null {
  const accelerator = defaultAccelerator(accelerators) ?? availableAccelerators(accelerators)[0] ?? null;
  const count = accelerator?.counts[0];
  return accelerator !== null && count !== undefined
    ? { acceleratorClass: accelerator.acceleratorClass, acceleratorCount: count }
    : null;
}

export function selectionIsAvailable(
  accelerators: Accelerator[],
  selection: AcceleratorSelection | null,
): boolean {
  if (selection === null) return false;
  const accelerator = accelerators.find(
    (candidate) => candidate.acceleratorClass === selection.acceleratorClass,
  );
  return accelerator?.available === true && accelerator.counts.includes(selection.acceleratorCount);
}

export function formatMedianDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 90) return `~${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `~${hours} h` : `~${hours} h ${remainder} min`;
}

/**
 * The figure a machine shows: the projection for this training set when the API
 * derived one, otherwise the median of past runs on the machine. Null when
 * nothing has finished on it — an estimate is never invented.
 */
export function formatMedianEstimate(estimate: AcceleratorEstimate): string | null {
  if (estimate.state !== "measured") return null;
  const projection = estimate.projection;
  if (projection !== null) {
    if (projection.estimatedCost === null) return null;
    return `${formatMedianDuration(projection.seconds)} · ~$${(
      projection.estimatedCost.amountCents / 100
    ).toFixed(2)}`;
  }
  if (estimate.estimatedCost === null) return null;
  return `${formatMedianDuration(estimate.medianSeconds)} · ~$${(
    estimate.estimatedCost.amountCents / 100
  ).toFixed(2)}`;
}

/** "Typically" for a median of past runs; "Projected" for this set's own figure. */
export function estimateLead(estimate: AcceleratorEstimate): string {
  return estimateProjection(estimate) === null ? "Typically" : "Projected";
}

export function formatSampleSize(sampleSize: number): string {
  return sampleSize === 1 ? "from 1 run so far" : `median of ${sampleSize} runs`;
}

/**
 * What the figure was derived from, in one line: for a projection, the measured
 * step time and the work this set implies UNDER THE RECIPE the run will use;
 * for a median, how many runs it is the median of. PRODUCT_MODEL.md Honesty —
 * every estimate is labelled with its basis, so a number for somebody else's
 * set, or for a recipe the customer has since changed, can never read as a
 * number for this run.
 */
export function formatEstimateBasis(estimate: AcceleratorEstimate): string {
  if (estimate.state !== "measured") return "No run has finished on this machine yet";
  const projection = estimate.projection;
  if (projection === null) return formatSampleSize(estimate.sampleSize);
  const runs = projection.sampleSize === 1 ? "1 finished run" : `${projection.sampleSize} finished runs`;
  const epochs = projection.epochs === 1 ? "1 epoch" : `${projection.epochs} epochs`;
  return (
    `Projected from ${runs} on this machine: ~${projection.stepSeconds.toFixed(1)} s per step` +
    ` × ${projection.steps.toLocaleString("en-US")} steps for your ${projection.examples.toLocaleString("en-US")} examples` +
    ` at ${epochs}, ${projection.maxSeqLen.toLocaleString("en-US")}-token window`
  );
}

export type TrainingClient = (
  path: string,
  init: RequestInit,
) => Promise<ModelEnvelope>;

/** Keeps the mocked acceptance seam on the same apiFetch call used in production. */
export function sendTrainingRequest(
  client: TrainingClient,
  body: TrainRequest,
): Promise<ModelEnvelope> {
  return client("/models", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}
